package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.AdminMenuDto
import com.qrkontaq.data.dto.DayCountDto
import com.qrkontaq.data.dto.HourCountDto
import com.qrkontaq.data.dto.LabelCountDto
import com.qrkontaq.data.dto.OrderDto
import com.qrkontaq.data.dto.PaymentDto
import com.qrkontaq.data.dto.RatingDto
import com.qrkontaq.data.dto.TopItemDto
import com.qrkontaq.data.dto.StaffDto
import com.qrkontaq.data.dto.StaffMemberDto
import com.qrkontaq.data.dto.SoldItemDto
import com.qrkontaq.data.dto.StatsDto
import com.qrkontaq.data.dto.SubscriptionInfoDto
import com.qrkontaq.data.dto.TableDto
import com.qrkontaq.data.dto.TablePositionDto
import com.qrkontaq.data.dto.WaiterCallDto
import com.qrkontaq.data.dto.currencySymbol
import com.qrkontaq.data.dto.localized
import com.qrkontaq.ui.components.notifications.LocalToastState
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.ui.components.forms.TextInput
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.tenant.MenuIcons
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.qrkontaq.utils.Storage
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import com.qrkontaq.data.dto.SubscriptionDto

// ---------- Dashboard ----------

internal enum class StatsPeriod { DAY, WEEK, MONTH, YEAR }

private fun periodTitle(period: StatsPeriod): String =
    when (period) {
        StatsPeriod.DAY -> AdminT.periodToday
        StatsPeriod.WEEK -> AdminT.periodWeek
        StatsPeriod.MONTH -> AdminT.periodMonth
        StatsPeriod.YEAR -> AdminT.periodYear
    }

private fun isoDate(d: kotlin.js.Date): String {
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "${d.getFullYear()}-$m-$day"
}

private fun shiftDays(d: kotlin.js.Date, days: Int): kotlin.js.Date =
    kotlin.js.Date(d.getFullYear(), d.getMonth(), d.getDate() + days)

/** ISO date bounds (inclusive) for a period; offset 0 = current, -1 = previous, ... */
internal fun rangeFor(period: StatsPeriod, offset: Int): Pair<String, String> {
    val now = kotlin.js.Date()
    return when (period) {
        StatsPeriod.DAY -> {
            val d = shiftDays(now, offset)
            isoDate(d) to isoDate(d)
        }
        StatsPeriod.WEEK -> {
            val monday = shiftDays(now, -((now.getDay() + 6) % 7) + offset * 7)
            isoDate(monday) to isoDate(shiftDays(monday, 6))
        }
        StatsPeriod.MONTH -> {
            val first = kotlin.js.Date(now.getFullYear(), now.getMonth() + offset, 1)
            val last = kotlin.js.Date(now.getFullYear(), now.getMonth() + offset + 1, 0)
            isoDate(first) to isoDate(last)
        }
        StatsPeriod.YEAR -> {
            val year = now.getFullYear() + offset
            "$year-01-01" to "$year-12-31"
        }
    }
}

private fun rangeLabel(period: StatsPeriod, offset: Int): String {
    val (from, to) = rangeFor(period, offset)
    fun dm(s: String) = "${s.substring(8, 10)}.${s.substring(5, 7)}"
    return when (period) {
        StatsPeriod.DAY -> "${dm(from)}.${from.take(4)}"
        StatsPeriod.WEEK -> "${dm(from)} \u2013 ${dm(to)}"
        StatsPeriod.MONTH -> "${from.substring(5, 7)}.${from.take(4)}"
        StatsPeriod.YEAR -> from.take(4)
    }
}

/** Fills every date in [from]..[to] with a count so charts have no gaps. */
private fun dailySeries(from: String, to: String, byDay: Map<String, Long>): List<Pair<String, Long>> {
    val result = mutableListOf<Pair<String, Long>>()
    var cursor = kotlin.js.Date(from.take(4).toInt(), from.substring(5, 7).toInt() - 1, from.substring(8, 10).toInt())
    var guard = 0
    while (guard++ < 400) {
        val iso = isoDate(cursor)
        if (iso > to) break
        result.add(iso to (byDay[iso] ?: 0L))
        cursor = shiftDays(cursor, 1)
    }
    return result
}

private fun monthlySeries(year: String, byDay: Map<String, Long>): List<Pair<String, Long>> {
    val byMonth = LinkedHashMap<String, Long>()
    (1..12).forEach { m -> byMonth[m.toString().padStart(2, '0')] = 0L }
    byDay.forEach { (date, count) ->
        if (date.take(4) == year) {
            val key = date.substring(5, 7)
            byMonth[key] = (byMonth[key] ?: 0L) + count
        }
    }
    return byMonth.map { (m, c) -> m to c }
}

// ---------- Demo data (local preview only) ----------

private fun isLocalDev(): Boolean =
    window.location.hostname == "localhost" || window.location.hostname == "127.0.0.1"

/** Deterministic [0,1) hash-noise: demo charts stay stable across reloads. */
private fun noise(seed: String): Double {
    var h = -2128831035
    for (ch in seed) {
        h = (h xor ch.code) * 16777619
    }
    return (kotlin.math.abs(h) % 10000) / 10000.0
}

/** Typical restaurant hour profile: lunch bump, strong dinner peak, dead night. */
private val HOUR_PROFILE = listOf(
    0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.02, 0.08, 0.18, 0.32, 0.62,
    0.9, 1.0, 0.72, 0.48, 0.42, 0.55, 0.82, 1.0, 0.88, 0.58, 0.28, 0.08,
)

/** Plausible generated stats for a date range, deterministic per restaurant+date. */
private fun demoStats(
    rid: String,
    from: String,
    to: String,
    menuItems: List<com.qrkontaq.data.dto.AdminMenuItemDto>,
): StatsDto {
    val dates = dailySeries(from, to, emptyMap()).map { it.first }

    val scansByDay = dates.map { date ->
        val d = kotlin.js.Date(date.take(4).toInt(), date.substring(5, 7).toInt() - 1, date.substring(8, 10).toInt())
        val weekendBoost = when (d.getDay()) {
            5 -> 1.45 // fri
            6 -> 1.8 // sat
            0 -> 1.4 // sun
            else -> 1.0
        }
        // Slow seasonal wave so month/year views are not flat.
        val season = 1.0 + 0.25 * kotlin.math.sin(2 * kotlin.math.PI * (d.getMonth() + 1) / 12.0)
        val base = 22 + 46 * noise("$rid/$date")
        DayCountDto(date, (base * weekendBoost * season).toLong())
    }
    val total = scansByDay.sumOf { it.count }.coerceAtLeast(1L)

    val hourScale = total.toDouble() / HOUR_PROFILE.sum()
    val scansByHour = (0..23).mapNotNull { hour ->
        val jitter = 0.8 + 0.4 * noise("$rid/h$hour/$from")
        val count = (HOUR_PROFILE[hour] * hourScale * jitter).toLong()
        if (HOUR_PROFILE[hour] == 0.0) null else HourCountDto(hour, count)
    }

    val tableCount = 7
    val tableWeights = (1..tableCount).map { t -> 0.25 + noise("$rid/t$t") }
    val weightSum = tableWeights.sum()
    val scansByTable = (1..tableCount).map { t ->
        LabelCountDto("$t", (total * tableWeights[t - 1] / weightSum).toLong())
    }.sortedByDescending { it.count }

    val topItems = menuItems
        .sortedByDescending { noise("$rid/$from/${it.id}") }
        .take(5)
        .mapIndexed { index, item ->
            TopItemDto(item, (total * (0.22 - index * 0.035) * (0.8 + 0.4 * noise("$rid/$from/l${item.id}"))).toLong().coerceAtLeast(1))
        }

    // Roughly a third of scans convert to an order; spread across the menu.
    val soldItems = menuItems
        .map { item ->
            val qty = (total * 0.33 * (0.05 + 0.5 * noise("$rid/$from/s${item.id}"))).toLong()
            SoldItemDto(
                itemId = item.id,
                name = item.name.localized("uk"),
                qty = qty,
                revenueMinor = qty * item.priceMinor,
            )
        }
        .filter { it.qty > 0 }
        .sortedByDescending { it.revenueMinor }

    return StatsDto(
        from = from,
        to = to,
        scansByDay = scansByDay,
        scansByHour = scansByHour,
        scansByTable = scansByTable,
        visits = (total * (0.55 + 0.2 * noise("$rid/v$from"))).toLong(),
        topItems = topItems,
        soldItems = soldItems,
        revenueMinor = soldItems.sumOf { it.revenueMinor },
        rating = RatingDto(
            average = 4.2 + 0.7 * noise("$rid/r"),
            count = (total / 6).coerceAtLeast(3),
        ),
    )
}

/** Generated hall-call events for demo mode (waiter + bill), clustered around lunch and dinner. */
private fun demoCallEvents(rid: String, from: String, to: String): List<Pair<String, String>> {
    val events = mutableListOf<Pair<String, String>>()
    val hours = listOf(12, 13, 19, 20, 21, 18, 14, 22)
    dailySeries(from, to, emptyMap()).forEach { (date, _) ->
        val waiter = (2 + 6 * noise("$rid/cw$date")).toInt()
        repeat(waiter) { k ->
            events.add("${date}T${hours[k % hours.size].toString().padStart(2, '0')}:10:00" to "WAITER")
        }
        // Bill requests trail waiter calls: most tables ask for the bill once.
        val bill = (1 + 3 * noise("$rid/cb$date")).toInt()
        repeat(bill) { k ->
            events.add("${date}T${hours[(k + 3) % hours.size].toString().padStart(2, '0')}:40:00" to "BILL")
        }
    }
    return events
}

/** Minor units → "1234.50 ₴" without Int overflow on period-wide sums. */
private fun moneyMinorLong(minor: Long, currency: String): String {
    val whole = minor / 100
    val cents = (minor % 100).toInt()
    return "$whole.${cents.toString().padStart(2, '0')} ${currencySymbol(currency)}"
}

@Composable
fun DashboardTab(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    currency: String,
    onNavigate: (AdminTab) -> Unit,
) {
    var period by remember(rid) { mutableStateOf(StatsPeriod.MONTH) }
    var offset by remember(rid) { mutableStateOf(0) }
    var stats by remember(rid) { mutableStateOf<StatsDto?>(null) }
    var prevStats by remember(rid) { mutableStateOf<StatsDto?>(null) }
    // Waiter/bill call events as (createdAt, type) — charted per period below.
    var callEvents by remember(rid) { mutableStateOf<List<Pair<String, String>>>(emptyList()) }
    var demo by remember(rid) { mutableStateOf(false) }
    var subscription by remember(rid) { mutableStateOf<SubscriptionInfoDto?>(null) }

    LaunchedEffect(rid) {
        subscription = runCatching { repository.subscription(rid) }.getOrNull()
    }
    LaunchedEffect(rid, period, offset) {
        stats = null
        val (from, to) = rangeFor(period, offset)
val (pFrom, pTo) = rangeFor(period, offset - 1)
        // Backends without from/to support: retry the legacy unfiltered call so
        // the dashboard still shows its default window instead of nothing.
        val real = runCatching { repository.stats(rid, from, to) }.getOrNull()
            ?: runCatching { repository.stats(rid) }.getOrNull()
        val realPrev = runCatching { repository.stats(rid, pFrom, pTo) }.getOrNull()
        val noData = real == null ||
            (real.scansByDay.sumOf { it.count } == 0L && real.scansByHour.sumOf { it.count } == 0L)
        if (noData && isLocalDev()) {
            // Local preview with an empty database: fill the dashboard with
            // plausible generated numbers so the charts can be designed/tested.
            demo = true
            val menuItems =
                runCatching { repository.getMenu(rid) }.getOrNull()
                    ?.categories.orEmpty().flatMap { it.items }
            val demoTo = minOf(to, isoDate(kotlin.js.Date()))
            stats = demoStats(rid, from, demoTo, menuItems)
            prevStats = demoStats(rid, pFrom, pTo, menuItems)
            callEvents = demoCallEvents(rid, from, demoTo)
        } else {
            demo = false
            // Even with no data we render the (empty) dashboard, not an endless skeleton.
            stats = real ?: StatsDto(from = from, to = to)
            prevStats = realPrev
            callEvents =
                runCatching { repository.waiterCalls(rid) }.getOrNull().orEmpty()
                    .map { it.createdAt to it.type }
        }
    }

    InjectDashboardStyles()

    Div(attrs = { classes("qk-dash", "qk-fade-in") }) {
        val data = stats
        if (data == null) {
            // Hero placeholder keeps the page from jumping while stats load.
            Div(attrs = {
                classes("qk-skeleton")
                attr("style", "height:250px;border-radius:24px;margin-bottom:16px")
            }) {}
        } else {
            DashboardHero(data, prevStats, period, offset, currency)
        }

        // Subscription reminders sit right under the hero, above the charts.
        subscription?.subscription?.let { sub ->
            if (sub.status == "PAST_DUE") {
                DashboardBanner(
                    text = AdminT.pastDueBanner,
                    actionText = AdminT.pay,
                    tone = "danger",
                ) { onNavigate(AdminTab.SUBSCRIPTION) }
            }
        }

        PeriodBar(
            period = period,
            offset = offset,
            onPeriod = { period = it; offset = 0 },
            onOffset = { offset = it },
        )

        if (demo) {
            Box(Modifier.margin(bottom = 10.px)) { AdminBadge("demo data", "warning") }
        }

        if (data == null) {
            DashboardSkeleton()
        } else {
            DashboardCharts(data, callEvents, period, offset, currency)
        }
    }
}

/** Charts and lists below the hero; every section renders even with no data. */
@Composable
private fun DashboardCharts(
    data: StatsDto,
    callEvents: List<Pair<String, String>>,
    period: StatsPeriod,
    offset: Int,
    currency: String,
) {
    // Range bounds for the charts below. Peak hours already conveys the daily
    // rhythm, so the standalone scan-trend chart was dropped.
    val (from, _) = rangeFor(period, offset)
    val (_, to) = rangeFor(period, offset)
    // Charts stop at today — no empty "future" slots inside the current period.
    val todayIso = isoDate(kotlin.js.Date())
    val end = if (to > todayIso) todayIso else to

    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(14.px)) {
            Column(Modifier.styleModifier { property("flex", "1"); property("min-width", "260px") }) {
                AdminCard {
                    Box(Modifier.margin(bottom = 12.px)) { DashCardTitle(AdminIcons.Clock, AdminT.peakHours) }
                    HoursLineChart(data.scansByHour.associate { it.hour to it.count })
                    if (data.scansByHour.sumOf { it.count } == 0L) {
                        Span(attrs = { classes("qk-dash-note") }) { Text(AdminT.dashEmptyPeakHint) }
                    }
                }
            }
            Column(Modifier.styleModifier { property("flex", "1"); property("min-width", "260px") }) {
                AdminCard {
                    Box(Modifier.margin(bottom = 12.px)) { DashCardTitle(AdminIcons.QrCode, AdminT.scansByTable) }
                    if (data.scansByTable.isEmpty()) {
                        DashEmpty(AdminIcons.QrCode, AdminT.dashEmptyTables, AdminT.dashEmptyTablesHint)
                    } else {
                        val maxTable = data.scansByTable.maxOf { it.count }.coerceAtLeast(1)
                        val totalTable = data.scansByTable.sumOf { it.count }.coerceAtLeast(1)
                        data.scansByTable.take(8).forEachIndexed { index, entry ->
                            val share = (100.0 * entry.count / totalTable).toInt().coerceAtLeast(1)
                            val frac = entry.count.toDouble() / maxTable
                            val gradient = when {
                                frac >= 0.66 -> "linear-gradient(90deg,#0F9D6E,#17B981)"
                                frac >= 0.33 -> "linear-gradient(90deg,#E8A33D,#F0BE6B)"
                                else -> "linear-gradient(90deg,#E05B4C,#FF8A7A)"
                            }
                            Row(
                                Modifier.fillMaxWidth().margin(bottom = 10.px),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                SpanText(
                                    (index + 1).toString().padStart(2, '0'),
                                    Modifier
                                        .fontSize(15.px)
                                        .fontWeight(FontWeight.Bold)
                                        .color(if (index == 0) Brand.Green else Brand.Divider)
                                        .minWidth(26.px)
                                        .attrsModifier { classes("qk-display") },
                                )
                                Column(Modifier.fillMaxWidth()) {
                                    Row(
                                        Modifier.fillMaxWidth().margin(bottom = 3.px),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically,
                                    ) {
                                        SpanText(
                                            if (entry.label == "\u2014") AdminT.withoutTable else AdminT.tableN(entry.label),
                                            Modifier.fontSize(12.px).fontWeight(FontWeight.Medium)
                                                .color(Brand.Charcoal).attrsModifier { classes("qk-nowrap") },
                                        )
                                        SpanText(
                                            "${entry.count} \u00B7 $share%",
                                            Modifier.fontSize(11.px).fontWeight(FontWeight.SemiBold)
                                                .color(Brand.TextSecondary).attrsModifier { classes("qk-nowrap") },
                                        )
                                    }
                                    Box(
                                        Modifier.fillMaxWidth().height(8.px).borderRadius(999.px)
                                            .backgroundColor(Brand.WarmBackground),
                                    ) {
                                        Box(
                                            Modifier
                                                .fillMaxWidth((100 * entry.count / maxTable).toInt().coerceAtLeast(4).percent)
                                                .height(8.px)
                                                .borderRadius(999.px)
                                                .styleModifier { property("background", gradient) },
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
    }

    // Hall calls within the selected range, split by type: waiter vs bill.
    val callsInRange = callEvents.filter { (created, _) -> created.take(10) in from..to }
    run {
        val waiterTotal = callsInRange.count { it.second != "BILL" }
        val billTotal = callsInRange.count { it.second == "BILL" }
        // (label, waiter count, bill count) per bucket; stacked in the chart.
        fun split(subset: List<Pair<String, String>>): Pair<Long, Long> =
            subset.count { it.second != "BILL" }.toLong() to subset.count { it.second == "BILL" }.toLong()
        val callBuckets: List<Triple<String, Long, Long>> = when (period) {
            StatsPeriod.DAY -> (0..23).map { h ->
                val hh = h.toString().padStart(2, '0')
                val (w, b) = split(callsInRange.filter { it.first.length >= 13 && it.first.substring(11, 13) == hh })
                Triple("$h", w, b)
            }
            StatsPeriod.WEEK, StatsPeriod.MONTH -> {
                val names = listOf(
                    AdminT.dayMon, AdminT.dayTue, AdminT.dayWed, AdminT.dayThu,
                    AdminT.dayFri, AdminT.daySat, AdminT.daySun,
                )
                dailySeries(from, end, emptyMap()).mapIndexed { i, (date, _) ->
                    val label =
                        if (period == StatsPeriod.WEEK) names.getOrElse(i) { date.takeLast(2) }
                        else date.substring(8, 10).trimStart('0')
                    val (w, b) = split(callsInRange.filter { it.first.take(10) == date })
                    Triple(label, w, b)
                }
            }
            StatsPeriod.YEAR -> (1..12)
                .filter { m -> from.take(4) != todayIso.take(4) || m <= todayIso.substring(5, 7).toInt() }
                .map { m ->
                    val mm = m.toString().padStart(2, '0')
                    val (w, b) = split(callsInRange.filter { it.first.substring(5, 7) == mm })
                    Triple(mm, w, b)
                }
        }
        AdminCard {
            Row(
                Modifier.fillMaxWidth().margin(bottom = 8.px).flexWrap(FlexWrap.Wrap).gap(8.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                DashCardTitle(MenuIcons.Bell, AdminT.hallCalls)
                Row(Modifier.flexWrap(FlexWrap.Wrap).gap(6.px), verticalAlignment = Alignment.CenterVertically) {
                    AdminBadge("${AdminT.legendWaiter} · $waiterTotal", if (waiterTotal > 0) "success" else "neutral")
                    AdminBadge("${AdminT.legendBill} · $billTotal", if (billTotal > 0) "warning" else "neutral")
                }
            }
            ChartSvg(stackedBarChartSvg(callBuckets))
            if (callsInRange.isEmpty()) {
                Span(attrs = { classes("qk-dash-note") }) { Text(AdminT.dashEmptyCallsHint) }
            }
        }
    }

    // Sold products: how many of each were ordered and total revenue per item.
    AdminCard {
            Row(
                Modifier.fillMaxWidth().margin(bottom = 10.px).flexWrap(FlexWrap.Wrap).gap(8.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                DashCardTitle(AdminIcons.Wallet, AdminT.soldProducts)
                AdminBadge(
                    moneyMinorLong(data.revenueMinor, currency),
                    if (data.revenueMinor > 0) "success" else "neutral",
                )
            }
            if (data.soldItems.isEmpty()) {
                DashEmpty(AdminIcons.Wallet, AdminT.dashEmptySales, AdminT.dashEmptySalesHint)
            }
            val maxRevenue = data.soldItems.maxOfOrNull { it.revenueMinor }?.coerceAtLeast(1L) ?: 1L
            data.soldItems.forEach { sold ->
                Row(
                    Modifier.fillMaxWidth().margin(bottom = 10.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(
                        Modifier.size(30.px).minWidth(30.px).borderRadius(9.px)
                            .backgroundColor(Brand.GreenSoft).margin(right = 10.px),
                        contentAlignment = Alignment.Center,
                    ) {
                        SpanText(
                            "${sold.qty}",
                            Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).color(Brand.Green)
                                .attrsModifier { classes("qk-display") },
                        )
                    }
                    Column(Modifier.fillMaxWidth()) {
                        Row(
                            Modifier.fillMaxWidth().margin(bottom = 3.px),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            SpanText(
                                sold.name,
                                Modifier.fontSize(14.px).fontWeight(FontWeight.Medium).color(Brand.Charcoal),
                            )
                            SpanText(
                                moneyMinorLong(sold.revenueMinor, currency),
                                Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold)
                                    .color(Brand.TextSecondary).attrsModifier { classes("qk-nowrap") },
                            )
                        }
                        Box(
                            Modifier.fillMaxWidth().height(8.px).borderRadius(999.px)
                                .backgroundColor(Brand.WarmBackground),
                        ) {
                            Box(
                                Modifier
                                    .fillMaxWidth((100 * sold.revenueMinor / maxRevenue).toInt().coerceAtLeast(4).percent)
                                    .height(8.px)
                                    .borderRadius(999.px)
                                    .styleModifier { property("background", "linear-gradient(90deg,#0F9D6E,#17B981)") },
                            )
                        }
                    }
                }
            }
    }

    AdminCard {
            Box(Modifier.margin(bottom = 12.px)) { DashCardTitle(MenuIcons.HeartFilled, AdminT.topDishes) }
            if (data.topItems.isEmpty()) {
                DashEmpty(MenuIcons.HeartFilled, AdminT.dashEmptyTop, AdminT.dashEmptyTopHint)
            }
            data.topItems.forEachIndexed { index, top ->
                Row(
                    Modifier.fillMaxWidth().margin(bottom = 10.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        SpanText(
                            "${index + 1}",
                            Modifier
                                .fontSize(17.px)
                                .fontWeight(FontWeight.Bold)
                                .color(
                                    when (index) {
                                        0 -> Brand.Amber
                                        1 -> Color.rgb(168, 176, 185)
                                        2 -> Color.rgb(201, 130, 43)
                                        else -> Brand.Divider
                                    },
                                )
                                .minWidth(24.px)
                                .attrsModifier { classes("qk-display") },
                        )
                        top.item.photoUrl?.let { photo ->
                            Image(
                                src = photo,
                                modifier = Modifier.size(38.px).minWidth(38.px).borderRadius(10.px)
                                    .objectFit(ObjectFit.Cover).margin(right = 10.px),
                            )
                        }
                        SpanText(
                            top.item.name.localized("uk"),
                            Modifier.fontSize(14.px).color(Brand.Charcoal),
                        )
                    }
                    Row(
                        Modifier
                            .backgroundColor(Brand.CoralSoft)
                            .borderRadius(999.px)
                            .padding(topBottom = 4.px, leftRight = 10.px),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        InlineIcon(MenuIcons.HeartFilled, 11, "#E05B4C")
                        SpanText(
                            "${top.likes}",
                            Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold)
                                .color(Brand.CoralText).margin(left = 5.px),
                        )
                    }
                }
            }
    }
}

/** Period picker: segmented granularity + prev/next stepper with the concrete range. */
@Composable
internal fun PeriodBar(
    period: StatsPeriod,
    offset: Int,
    onPeriod: (StatsPeriod) -> Unit,
    onOffset: (Int) -> Unit,
) {
    Row(
        Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(8.px).margin(bottom = 14.px),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            Modifier
                .backgroundColor(Brand.Surface)
                .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, Brand.Divider)
                .borderRadius(999.px)
                .padding(3.px),
        ) {
            StatsPeriod.entries.forEach { p ->
                val selected = p == period
                Box(
                    Modifier
                        .borderRadius(999.px)
                        .padding(topBottom = 6.px, leftRight = 13.px)
                        .cursor(Cursor.Pointer)
                        .then(if (selected) Modifier.backgroundColor(Brand.Emerald) else Modifier)
                        .attrsModifier { classes("qk-press"); attr("role", "button") }
                        .onClick { onPeriod(p) },
                ) {
                    SpanText(
                        periodTitle(p),
                        Modifier
                            .fontSize(12.px)
                            .fontWeight(if (selected) FontWeight.SemiBold else FontWeight.Medium)
                            .color(if (selected) Brand.Surface else Brand.TextSecondary)
                            .attrsModifier { classes("qk-nowrap") },
                    )
                }
            }
        }

        Row(
            Modifier
                .backgroundColor(Brand.Surface)
                .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, Brand.Divider)
                .borderRadius(999.px)
                .padding(3.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            PeriodArrow(AdminIcons.ChevronLeft, true, AdminT.prevPeriod) { onOffset(offset - 1) }
            SpanText(
                rangeLabel(period, offset),
                Modifier
                    .fontSize(12.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal)
                    .padding(leftRight = 8.px)
                    .attrsModifier { classes("qk-nowrap") },
            )
            PeriodArrow(AdminIcons.ChevronRight, offset < 0, AdminT.nextPeriod) { onOffset(offset + 1) }
        }
    }
}

@Composable
private fun PeriodArrow(
    iconSvg: String,
    enabled: Boolean,
    label: String,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .size(26.px)
            .minWidth(26.px)
            .borderRadius(999.px)
            .then(if (enabled) Modifier.backgroundColor(Brand.WarmBackground).cursor(Cursor.Pointer) else Modifier)
            .attrsModifier {
                if (enabled) classes("qk-press")
                attr("role", "button")
                attr("aria-label", label)
                if (!enabled) attr("aria-disabled", "true")
            }
            .onClick { if (enabled) onClick() },
        contentAlignment = Alignment.Center,
    ) {
        InlineIcon(iconSvg, 13, if (enabled) "#6B7280" else "#D9D4CC")
    }
}

// ---------- Dashboard charts (hand-drawn SVG) ----------

/** Injects a prebuilt SVG string; scales to the container width. */
@Composable
private fun ChartSvg(svg: String, maxWidthPx: Int? = null) {
    Div(
        attrs = {
            attr("style", "width:100%" + (maxWidthPx?.let { ";max-width:${it}px" } ?: ""))
        },
    ) {
        // Keyed on the markup so late-arriving data (e.g. call events loading
        // after the stats) re-renders the chart; a ref{} would fire only once.
        DisposableEffect(svg) {
            scopeElement.innerHTML = svg
            onDispose { scopeElement.innerHTML = "" }
        }
    }
}

private fun fmt(v: Double): String {
    val r = kotlin.math.round(v * 10) / 10
    val i = r.toInt()
    return if (r == i.toDouble()) "$i" else "$r"
}

private fun xmlEscape(s: String) = s.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;")

/**
 * Emerald hero in the Menu-page style: page identity on a green gradient,
 * scan total for the range with a trend pill, a mint sparkline and the four
 * key numbers as frosted glass chips.
 */
@Composable
private fun DashboardHero(
    data: StatsDto,
    prev: StatsDto?,
    period: StatsPeriod,
    offset: Int,
    currency: String,
) {
    val total = data.scansByDay.sumOf { it.count }
        .let { if (it == 0L) data.scansByHour.sumOf { h -> h.count } else it }
    val prevTotal = prev?.let { p ->
        p.scansByDay.sumOf { it.count }.let { if (it == 0L) p.scansByHour.sumOf { h -> h.count } else it }
    }
    val delta = if (prevTotal != null && prevTotal > 0) (((total - prevTotal) * 100) / prevTotal).toInt() else null

    val spark: List<Long> = when (period) {
        StatsPeriod.DAY -> (0..23).map { h -> data.scansByHour.firstOrNull { it.hour == h }?.count ?: 0L }
        StatsPeriod.YEAR -> {
            val byDay = data.scansByDay.associate { it.date to it.count }
            val year = data.scansByDay.firstOrNull()?.date?.take(4)
            year?.let { y -> monthlySeries(y, byDay).map { it.second }.dropLastWhile { it == 0L } }
                ?: emptyList()
        }
        else -> data.scansByDay.map { it.count }
    }

    Div(attrs = { classes("qk-dash-hero", "qk-rise") }) {
        Div(attrs = { classes("qk-dash-hero-head") }) {
            Div(attrs = { classes("ic") }) { InlineIcon(AdminIcons.Dashboard, 26, "#FFFFFF") }
            Div {
                H1(attrs = { classes("qk-dash-disp") }) { Text(AdminT.tabDashboard) }
                P { Text(AdminT.tabDashboardSub) }
            }
            // The interface-language switch lives in the hero now that the
            // dashboard has no plain page header above it.
            Div(attrs = { classes("lang") }) { AdminLangDropdown(dark = true) }
        }
        Div(attrs = { classes("qk-dash-main") }) {
            Div {
                Span(attrs = { classes("qk-dash-over") }) {
                    Text("${AdminT.statScans} \u00B7 ${rangeLabel(period, offset)}")
                }
                Div(attrs = { classes("qk-dash-numrow") }) {
                    Span(attrs = { classes("qk-dash-num") }) { Text("$total") }
                    delta?.let { d ->
                        val up = d >= 0
                        Span(attrs = { classes("qk-dash-delta", if (up) "up" else "down") }) {
                            Text(if (up) "\u25B2 +$d%" else "\u25BC $d%")
                        }
                    }
                }
            }
            if (spark.size >= 2 && spark.any { it > 0 }) {
                Div(attrs = { classes("qk-dash-spark") }) { ChartSvg(sparklineSvg(spark)) }
            }
        }
        Div(attrs = { classes("qk-dash-chips") }) {
            DashChip(AdminIcons.Wallet, moneyMinorLong(data.revenueMinor, currency), AdminT.statRevenue)
            DashChip(AdminIcons.Footprints, data.visits.toString(), AdminT.statVisits)
            DashChip(
                MenuIcons.StarFilled,
                data.rating.average?.let { avg -> "${(avg * 10).toInt() / 10.0}" } ?: "\u2014",
                AdminT.statRating,
            )
            DashChip(MenuIcons.HeartFilled, data.topItems.sumOf { it.likes }.toString(), AdminT.statDishLikes)
        }
    }
}

/** Frosted glass stat chip inside the hero. */
@Composable
private fun DashChip(iconSvg: String, value: String, label: String) {
    Div(attrs = { classes("qk-dash-chip") }) {
        Div(attrs = { classes("i") }) { InlineIcon(iconSvg, 16, "#FFFFFF") }
        Div {
            B { Text(value) }
            Span { Text(label) }
        }
    }
}

/** Card section title with a small emerald icon chip. */
@Composable
private fun DashCardTitle(iconSvg: String, text: String) {
    Div(attrs = { classes("qk-dash-ct") }) {
        I { InlineIcon(iconSvg, 16, "#0B7A55") }
        Span { Text(text) }
    }
}

/** Compact dashed empty state inside a dashboard card. */
@Composable
private fun DashEmpty(iconSvg: String, title: String, hint: String) {
    Div(attrs = { classes("qk-dash-empty") }) {
        Div(attrs = { classes("ic") }) { InlineIcon(iconSvg, 22, "#0B7A55") }
        B { Text(title) }
        Span { Text(hint) }
    }
}

/** Catmull-Rom smoothing converted to cubic beziers. */
private fun smoothPath(px: DoubleArray, py: DoubleArray): String {
    val n = px.size
    val path = StringBuilder("M ${fmt(px[0])} ${fmt(py[0])}")
    for (i in 0 until n - 1) {
        val i0 = maxOf(i - 1, 0)
        val i3 = minOf(i + 2, n - 1)
        val c1x = px[i] + (px[i + 1] - px[i0]) / 6.0
        val c1y = py[i] + (py[i + 1] - py[i0]) / 6.0
        val c2x = px[i + 1] - (px[i3] - px[i]) / 6.0
        val c2y = py[i + 1] - (py[i3] - py[i]) / 6.0
        path.append(" C ${fmt(c1x)} ${fmt(c1y)}, ${fmt(c2x)} ${fmt(c2y)}, ${fmt(px[i + 1])} ${fmt(py[i + 1])}")
    }
    return path.toString()
}

/** Small mint sparkline for the hero card. */
private fun sparklineSvg(counts: List<Long>): String {
    if (counts.size < 2) return ""
    val w = 230.0
    val h = 72.0
    val pad = 8.0
    val max = counts.max().coerceAtLeast(1L)
    val n = counts.size
    val px = DoubleArray(n) { i -> pad + (w - pad * 2) * i / (n - 1) }
    val py = DoubleArray(n) { i -> pad + (h - pad * 2) * (1.0 - counts[i].toDouble() / max) }
    val line = smoothPath(px, py)
    val area = "$line L ${fmt(px[n - 1])} ${fmt(h - 2)} L ${fmt(px[0])} ${fmt(h - 2)} Z"
    return """<svg viewBox="0 0 ${w.toInt()} ${h.toInt()}" width="100%" xmlns="http://www.w3.org/2000/svg" style="display:block">
<defs>
<linearGradient id="qkSparkFill" x1="0" y1="0" x2="0" y2="1">
<stop offset="0%" stop-color="#7EE2BC" stop-opacity=".35"/>
<stop offset="100%" stop-color="#7EE2BC" stop-opacity="0"/>
</linearGradient>
</defs>
<path d="$area" fill="url(#qkSparkFill)"/>
<path d="$line" fill="none" stroke="#7EE2BC" stroke-width="2.5" stroke-linecap="round"/>
<circle cx="${fmt(px[n - 1])}" cy="${fmt(py[n - 1])}" r="3.5" fill="#7EE2BC" stroke="#0B7A55" stroke-width="2"/>
</svg>"""
}

/**
 * Stacked call bars per bucket: waiter calls (emerald) with bill requests
 * (amber) stacked on top. Empty buckets get a light gray stub.
 */
private fun stackedBarChartSvg(items: List<Triple<String, Long, Long>>): String {
    if (items.isEmpty()) return ""
    val w = 600.0
    val h = 170.0
    val padL = 8.0
    val padR = 12.0
    val padT = 20.0
    val padB = 22.0
    val baseY = h - padB
    val n = items.size
    val max = items.maxOf { it.second + it.third }.coerceAtLeast(1L)
    val slot = (w - padL - padR) / n
    val barW = (slot * 0.52).coerceAtMost(24.0).coerceAtLeast(3.0)
    val labelEvery = if (n <= 12) 1 else (n + 9) / 10

    val bars = StringBuilder()
    val texts = StringBuilder()
    for (i in 0 until n) {
        val (label, waiter, bill) = items[i]
        val total = waiter + bill
        val cx = padL + slot * i + slot / 2
        val tooltip = "$label: ${AdminT.legendWaiter} $waiter · ${AdminT.legendBill} $bill"
        if (total == 0L) {
            bars.append(
                """<rect x="${fmt(cx - barW / 2)}" y="${fmt(baseY - 3)}" width="${fmt(barW)}" height="3" rx="1.5" fill="#ECE8E2"><title>${xmlEscape(tooltip)}</title></rect>""",
            )
        } else {
            val totalH = ((baseY - padT) * total / max).coerceAtLeast(6.0)
            val billH = totalH * bill / total
            val waiterH = totalH - billH
            val rx = minOf(4.0, barW / 2, totalH / 2)
            // One group so the whole stack shares the tooltip.
            bars.append("""<g><title>${xmlEscape(tooltip)}</title>""")
            if (waiterH > 0) {
                bars.append(
                    """<rect x="${fmt(cx - barW / 2)}" y="${fmt(baseY - waiterH)}" width="${fmt(barW)}" height="${fmt(waiterH)}" rx="${fmt(if (bill == 0L) rx else 0.0)}" fill="url(#qkBarWaiter)"/>""",
                )
            }
            if (billH > 0) {
                bars.append(
                    """<rect x="${fmt(cx - barW / 2)}" y="${fmt(baseY - totalH)}" width="${fmt(barW)}" height="${fmt(billH)}" rx="${fmt(rx)}" fill="url(#qkBarBill)"/>""",
                )
            }
            bars.append("</g>")
            if (n <= 12) {
                texts.append(
                    """<text x="${fmt(cx)}" y="${fmt(baseY - totalH - 6)}" text-anchor="middle" font-size="9.5" font-weight="600" fill="#6B7280">$total</text>""",
                )
            }
        }
        if (i % labelEvery == 0 || i == n - 1) {
            texts.append(
                """<text x="${fmt(cx)}" y="${fmt(h - 6)}" text-anchor="middle" font-size="9.5" fill="#9CA3AF">${xmlEscape(label)}</text>""",
            )
        }
    }

    return """<svg viewBox="0 0 ${w.toInt()} ${h.toInt()}" width="100%" xmlns="http://www.w3.org/2000/svg" style="display:block">
<defs>
<linearGradient id="qkBarWaiter" x1="0" y1="0" x2="0" y2="1">
<stop offset="0%" stop-color="#17B981"/>
<stop offset="100%" stop-color="#0F9D6E"/>
</linearGradient>
<linearGradient id="qkBarBill" x1="0" y1="0" x2="0" y2="1">
<stop offset="0%" stop-color="#F0BE6B"/>
<stop offset="100%" stop-color="#E8A33D"/>
</linearGradient>
</defs>
<line x1="${fmt(padL)}" y1="${fmt(baseY)}" x2="${fmt(w - padR + 4)}" y2="${fmt(baseY)}" stroke="#ECE8E2" stroke-width="1.5"/>
$bars
$texts
</svg>"""
}

/** Smooth 24-hour scan curve with an area fill; the peak hour gets a dot and a flame note. */
@Composable
private fun HoursLineChart(byHour: Map<Int, Long>) {
    ChartSvg(hourLineChartSvg(byHour))
    byHour.maxByOrNull { it.value }?.takeIf { it.value > 0 }?.let {
        Row(Modifier.margin(top = 8.px), verticalAlignment = Alignment.CenterVertically) {
            InlineIcon(MenuIcons.Flame, 13, "#0F9D6E")
            SpanText(
                "${it.key}:00 \u00B7 ${it.value}",
                Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal).margin(left = 5.px),
            )
        }
    }
}

private fun hourLineChartSvg(byHour: Map<Int, Long>): String {
    val w = 600.0
    val h = 170.0
    val padL = 8.0
    val padR = 12.0
    val padT = 18.0
    val padB = 22.0
    val baseY = h - padB
    val counts = (0..23).map { byHour[it] ?: 0L }
    val max = counts.max().coerceAtLeast(1L)
    val n = counts.size
    val px = DoubleArray(n) { i -> padL + (w - padL - padR) * i / (n - 1) }
    val py = DoubleArray(n) { i -> padT + (baseY - padT) * (1.0 - counts[i].toDouble() / max) }
    val line = smoothPath(px, py)
    val area = "$line L ${fmt(px[n - 1])} ${fmt(baseY)} L ${fmt(px[0])} ${fmt(baseY)} Z"

    val peakIdx = counts.indices.maxByOrNull { counts[it] } ?: 0
    val peakDot = if (counts[peakIdx] > 0) {
        """<circle cx="${fmt(px[peakIdx])}" cy="${fmt(py[peakIdx])}" r="4" fill="#0F9D6E" stroke="#FFFFFF" stroke-width="2"/>"""
    } else {
        ""
    }

    // Invisible hover slots so every hour reports "H:00 \u2014 N".
    val hover = StringBuilder()
    val slot = (w - padL - padR) / (n - 1)
    for (i in 0 until n) {
        hover.append(
            """<rect x="${fmt(px[i] - slot / 2)}" y="0" width="${fmt(slot)}" height="${fmt(baseY)}" fill="transparent"><title>$i:00 \u2014 ${counts[i]}</title></rect>""",
        )
    }

    val labels = listOf(0, 6, 12, 18, 23).joinToString("") { i ->
        """<text x="${fmt(px[i])}" y="${fmt(h - 6)}" text-anchor="middle" font-size="9.5" fill="#9CA3AF">$i</text>"""
    }

    return """<svg viewBox="0 0 ${w.toInt()} ${h.toInt()}" width="100%" xmlns="http://www.w3.org/2000/svg" style="display:block">
<defs>
<linearGradient id="qkHourFill" x1="0" y1="0" x2="0" y2="1">
<stop offset="0%" stop-color="#0F9D6E" stop-opacity=".28"/>
<stop offset="100%" stop-color="#0F9D6E" stop-opacity="0"/>
</linearGradient>
</defs>
<line x1="${fmt(padL)}" y1="${fmt(baseY)}" x2="${fmt(w - padR + 4)}" y2="${fmt(baseY)}" stroke="#ECE8E2" stroke-width="1.5"/>
<path d="$area" fill="url(#qkHourFill)"/>
<path d="$line" fill="none" stroke="#0F9D6E" stroke-width="2.5" stroke-linecap="round"/>
$peakDot
$labels
${hover}
</svg>"""
}

private fun layoutKey(rid: String) = "table_layout_$rid"

private fun loadLayout(rid: String): Map<String, Pair<Double, Double>> =
    Storage.get(layoutKey(rid))?.split(";")?.mapNotNull { entry ->
        val parts = entry.split(":")
        val x = parts.getOrNull(1)?.toDoubleOrNull()
        val y = parts.getOrNull(2)?.toDoubleOrNull()
        if (parts.size == 3 && x != null && y != null) parts[0] to (x to y) else null
    }?.toMap().orEmpty()

private fun saveLayout(rid: String, layout: Map<String, Pair<Double, Double>>) {
    Storage.save(layoutKey(rid), layout.entries.joinToString(";") { "${it.key}:${fmt(it.value.first)}:${fmt(it.value.second)}" })
}

private fun defaultPlanPos(index: Int): Pair<Double, Double> =
    (12.0 + (index % 5) * 19.0) to (16.0 + (index / 5) * 24.0)


private fun pointerXY(e: org.w3c.dom.events.Event): Pair<Double, Double> {
    val d = e.asDynamic()
    return if (d.touches != null && d.touches.length as Int > 0) {
        (d.touches[0].clientX.unsafeCast<Double>()) to (d.touches[0].clientY.unsafeCast<Double>())
    } else {
        (d.clientX.unsafeCast<Double>()) to (d.clientY.unsafeCast<Double>())
    }
}

/** Draggable hall map: round tables coloured by activity, pulsing on open calls. */
@Composable
private fun TableFloorPlan(
    rid: String,
    tables: List<TableDto>,
    calls: List<WaiterCallDto>,
    orders: Map<String, List<OrderDto>>,
    canDrag: Boolean,
    onOpen: ((TableDto) -> Unit)?,
    // Fires with the full layout after a drag so the caller can persist it globally.
    onLayoutSave: ((Map<String, Pair<Double, Double>>) -> Unit)? = null,
) {
    var layout by remember(rid) { mutableStateOf(loadLayout(rid)) }
    // Backend positions win over the per-browser cache: every device sees the same plan.
    LaunchedEffect(tables) {
        val server =
            tables.mapNotNull { t ->
                val x = t.posX
                val y = t.posY
                if (x != null && y != null) t.id to (x to y) else null
            }
        if (server.isNotEmpty()) layout = layout + server
    }
    var containerEl by remember { mutableStateOf<org.w3c.dom.HTMLElement?>(null) }
    var draggingId by remember { mutableStateOf<String?>(null) }
    var dragMoved by remember { mutableStateOf(false) }
    var startX by remember { mutableStateOf(0.0) }
    var startY by remember { mutableStateOf(0.0) }

    DisposableEffect(draggingId) {
        val id = draggingId
        if (id == null) {
            onDispose { }
        } else {
            val move: (org.w3c.dom.events.Event) -> Unit = { e ->
                val (cx, cy) = pointerXY(e)
                if (kotlin.math.abs(cx - startX) + kotlin.math.abs(cy - startY) > 6) dragMoved = true
                if (dragMoved) {
                    containerEl?.let { el ->
                        val rect = el.getBoundingClientRect()
                        val x = ((cx - rect.left) / rect.width * 100).coerceIn(5.0, 95.0)
                        val y = ((cy - rect.top) / rect.height * 100).coerceIn(8.0, 92.0)
                        layout = layout + (id to (x to y))
                    }
                }
            }
            val up: (org.w3c.dom.events.Event) -> Unit = {
                if (dragMoved) {
                    saveLayout(rid, layout)
                    onLayoutSave?.invoke(layout)
                } else {
                    tables.firstOrNull { it.id == id }?.let { onOpen?.invoke(it) }
                }
                draggingId = null
            }
            window.addEventListener("mousemove", move)
            window.addEventListener("mouseup", up)
            window.addEventListener("touchmove", move)
            window.addEventListener("touchend", up)
            onDispose {
                window.removeEventListener("mousemove", move)
                window.removeEventListener("mouseup", up)
                window.removeEventListener("touchmove", move)
                window.removeEventListener("touchend", up)
            }
        }
    }

    Box(
        Modifier
            .fillMaxWidth()
            .borderRadius(16.px)
            .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, Brand.Divider)
            .margin(bottom = 10.px)
            .position(org.jetbrains.compose.web.css.Position.Relative)
            .styleModifier {
                // A roomy floor plan: tall enough to lay out a real hall, still
                // bounded by the viewport-derived middle term on large screens.
                property("height", "clamp(560px, calc(100dvh - 330px), 880px)")
                property("background-color", "#FAF8F5")
                property("background-image", "radial-gradient(circle, #E5E0D8 1px, transparent 1px)")
                property("background-size", "26px 26px")
                property("overflow", "hidden")
                // Managers drag tables, so their touches must not scroll the page;
                // for everyone else the plan is a plain tap surface.
                if (canDrag) property("touch-action", "none")
            }
            .attrsModifier {
                ref { el ->
                    containerEl = el as? org.w3c.dom.HTMLElement
                    onDispose { containerEl = null }
                }
            },
    ) {
        tables.forEachIndexed { index, table ->
            val pos = layout[table.id] ?: defaultPlanPos(index)
            val call = calls.firstOrNull { it.tableLabel == table.label }
            val tableOrders = orders[table.id].orEmpty()
            val ready = tableOrders.any { it.status == "READY" }
            val occupied = tableOrders.isNotEmpty()
            // A table with active orders the kitchen hasn't finished — "waiting for cooking".
            val waiting = occupied && !ready
            val topBg = when {
                call != null -> "linear-gradient(135deg,#FF8A7A,#E05B4C)"
                ready -> "linear-gradient(135deg,#17B981,#0F9D6E)"
                waiting -> "linear-gradient(135deg,#F2B65A,#E8A33D)"
                else -> "#EDE9E2"
            }
            // Amber (and the free state) read better with dark text; coral/green use white.
            val onDark = call != null || ready
            val chairColor = if (occupied || call != null) "rgba(31,36,33,.4)" else "rgba(31,36,33,.16)"

            // Node = chairs around a tabletop; the whole figure drags and opens the table.
            Box(
                Modifier
                    .position(org.jetbrains.compose.web.css.Position.Absolute)
                    .size(80.px)
                    .cursor(if (canDrag) Cursor.Grab else if (onOpen != null) Cursor.Pointer else Cursor.Default)
                    .styleModifier {
                        property("left", "${fmt(pos.first)}%")
                        property("top", "${fmt(pos.second)}%")
                        property("transform", "translate(-50%, -50%)")
                        property("user-select", "none")
                        if (canDrag) property("touch-action", "none")
                    }
                    .attrsModifier {
                        attr("title", table.label)
                        if (canDrag) {
                            onMouseDown { evt ->
                                draggingId = table.id
                                dragMoved = false
                                val (px, py) = pointerXY(evt.nativeEvent)
                                startX = px
                                startY = py
                            }
                            onTouchStart { evt ->
                                draggingId = table.id
                                dragMoved = false
                                val (px, py) = pointerXY(evt.nativeEvent)
                                startX = px
                                startY = py
                            }
                        }
                    }
                    .then(
                        if (!canDrag && onOpen != null) {
                            Modifier.onClick { onOpen(table) }
                        } else {
                            Modifier
                        },
                    ),
                contentAlignment = Alignment.Center,
            ) {
                // Chairs
                listOf(
                    "left:50%;top:2px;transform:translateX(-50%);width:18px;height:6px",
                    "left:50%;bottom:2px;transform:translateX(-50%);width:18px;height:6px",
                    "left:2px;top:50%;transform:translateY(-50%);width:6px;height:18px",
                    "right:2px;top:50%;transform:translateY(-50%);width:6px;height:18px",
                ).forEach { css ->
                    Box(
                        Modifier
                            .position(org.jetbrains.compose.web.css.Position.Absolute)
                            .borderRadius(4.px)
                            .styleModifier {
                                css.split(";").forEach { rule ->
                                    val (k, v) = rule.split(":")
                                    property(k, v)
                                }
                                property("background", chairColor)
                            },
                    )
                }

                // Tabletop
                Box(
                    Modifier
                        .size(56.px)
                        .borderRadius(999.px)
                        .styleModifier {
                            property("background", topBg)
                            property("box-shadow", "0 4px 12px rgba(31,36,33,.18)")
                        }
                        .attrsModifier {
                            if (call != null) classes("qk-pulse")
                            if (call == null && ready) classes("qk-pulse-green")
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        SpanText(
                            tableZone(table.label).second,
                            Modifier
                                .fontSize(16.px)
                                .fontWeight(FontWeight.Bold)
                                .color(if (onDark) Brand.Surface else Brand.Charcoal)
                                .attrsModifier { classes("qk-display") },
                        )
                        when {
                            call != null -> InlineIcon(MenuIcons.Bell, 11, "#FFFFFF")
                            ready -> InlineIcon(AdminIcons.ChefHat, 11, "#FFFFFF")
                            // Waiting for the kitchen: just a flame, no price.
                            occupied -> InlineIcon(AdminIcons.Flame, 13, "#7A4E12")
                            else -> {}
                        }
                    }
                }

            }
        }
    }
}

/** Splits a table label into (zone, short label): "Тераса 2" → ("Тераса", "2"); "5" → ("", "5"). */
private fun tableZone(label: String): Pair<String, String> {
    val trimmed = label.trim()
    val match = Regex("^(.*\\D)\\s*(\\d+)$").find(trimmed)
    return when {
        match != null && match.groupValues[1].isNotBlank() ->
            match.groupValues[1].trim().trimEnd('-', '·', '#').trim() to match.groupValues[2]
        trimmed.isNotEmpty() && trimmed.all { it.isDigit() } -> "" to trimmed
        else -> trimmed to trimmed
    }
}

/** Open guest calls (waiter / bill); polling lives in TablesTab so the cards share it. */
@Composable
private fun WaiterCallsCard(
    calls: List<WaiterCallDto>,
    onResolve: (WaiterCallDto) -> Unit,
) {
    if (calls.isEmpty()) return

    // Table zone/card helpers live below; see tableZone() and TableCard().

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(16.px)
            .border(2.px, org.jetbrains.compose.web.css.LineStyle.Solid, Brand.Coral)
            .padding(16.px)
            .margin(bottom = 14.px)
            .attrsModifier { classes("qk-rise") },
    ) {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 10.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(com.qrkontaq.ui.tenant.MenuIcons.Bell, 17, "#E05B4C")
            SpanText(
                AdminT.hallCalls,
                Modifier.fontSize(16.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal).margin(left = 8.px),
            )
            Box(Modifier.margin(left = 8.px)) { AdminBadge("${calls.size}", "danger") }
        }
        calls.forEach { call ->
            Row(
                Modifier.fillMaxWidth().margin(bottom = 8.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(
                    Modifier.minWidth(0.px).margin(right = 10.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(
                        Modifier
                            .size(34.px)
                            .minWidth(34.px)
                            .borderRadius(999.px)
                            .backgroundColor(Brand.CoralSoft)
                            .margin(right = 10.px),
                        contentAlignment = Alignment.Center,
                    ) {
                        InlineIcon(
                            if (call.type == "BILL") AdminIcons.CreditCard else com.qrkontaq.ui.tenant.MenuIcons.Bell,
                            15,
                            "#E05B4C",
                        )
                    }
                    Column {
                        SpanText(
                            "${AdminT.tableN(call.tableLabel)} — " +
                                if (call.type == "BILL") AdminT.asksBill else AdminT.callsWaiter,
                            Modifier.fontSize(14.px).fontWeight(FontWeight.Medium).color(Brand.Charcoal),
                        )
                        SpanText(
                            call.createdAt.take(16).replace("T", " · "),
                            Modifier.fontSize(11.px).color(Brand.TextSecondary),
                        )
                    }
                }
                // The action must never get squeezed by long table labels: icon and text stay on one line.
                AdminButton(
                    AdminT.done,
                    iconSvg = AdminIcons.Check,
                    modifier = Modifier.flexShrink(0),
                    onClick = { onResolve(call) },
                )
            }
        }
    }
}

@Composable
private fun DashboardBanner(
    text: String,
    actionText: String,
    tone: String,
    onAction: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .backgroundColor(if (tone == "danger") Brand.CoralSoft else Color.rgba(232, 163, 61, 0.14f))
            .borderRadius(14.px)
            .padding(14.px)
            .margin(bottom = 14.px)
            .flexWrap(FlexWrap.Wrap)
            .gap(10.px),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text,
            Modifier
                .fontSize(13.px)
                .fontWeight(FontWeight.Medium)
                .color(if (tone == "danger") Brand.CoralText else Brand.Charcoal)
                .styleModifier { property("flex", "1"); property("min-width", "200px") },
        )
        AdminButton(actionText, onAction, danger = tone == "danger")
    }
}

@Composable
internal fun StatTile(
    iconSvg: String,
    label: String,
    value: String,
    iconColor: String,
    iconBg: Color,
) {
    Row(
        Modifier
            .backgroundColor(Brand.Surface)
            .borderRadius(16.px)
            .padding(14.px)
            .styleModifier { property("flex", "1"); property("min-width", "150px") }
            .boxShadow(offsetY = 2.px, blurRadius = 8.px, color = Color.rgba(31, 36, 33, 0.04f))
            .attrsModifier { classes("qk-adm-card", "qk-rise") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .size(42.px)
                .minWidth(42.px)
                .borderRadius(999.px)
                .backgroundColor(iconBg)
                .margin(right = 11.px),
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(iconSvg, 18, iconColor)
        }
        Column(Modifier.styleModifier { property("min-width", "0") }) {
            SpanText(
                value,
                Modifier.fontWeight(FontWeight.Bold).color(Brand.Charcoal)
                    .styleModifier {
                        property("line-height", "1.1")
                        // Long money values ("1234.56 €") shrink instead of wrapping.
                        property("font-size", "clamp(16px, 5vw, 24px)")
                        property("white-space", "nowrap")
                    }
                    .attrsModifier { classes("qk-display") },
            )
            SpanText(label, Modifier.fontSize(11.px).color(Brand.TextSecondary))
        }
    }
}

/** Chart-area placeholder shown under the hero while stats load. */
@Composable
private fun DashboardSkeleton() {
    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(14.px).margin(bottom = 14.px)) {
        repeat(2) {
            Box(
                Modifier.height(150.px).borderRadius(16.px)
                    .styleModifier { property("flex", "1"); property("min-width", "260px") }
                    .attrsModifier { classes("qk-skeleton") },
            )
        }
    }
    Box(Modifier.fillMaxWidth().height(180.px).borderRadius(16.px).attrsModifier { classes("qk-skeleton") })
}

// ---------- Stop-list ----------

@Composable
fun StopListTab(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
) {
    var menu by remember(rid) { mutableStateOf<AdminMenuDto?>(null) }
    var search by remember { mutableStateOf("") }
    val toast = LocalToastState.current

    fun reload() {
        scope.launch { menu = runCatching { repository.getMenu(rid) }.getOrNull() }
    }
    LaunchedEffect(rid) { reload() }

    if (menu == null) {
        repeat(5) {
            Box(
                Modifier.fillMaxWidth().height(62.px).borderRadius(14.px).margin(bottom = 8.px)
                    .attrsModifier { classes("qk-skeleton") },
            )
        }
        return
    }

    val allItems = menu?.categories.orEmpty().flatMap { it.items }
    val stopped = allItems.count { !it.isAvailable }

    Row(Modifier.fillMaxWidth().gap(8.px).margin(bottom = 10.px), verticalAlignment = Alignment.CenterVertically) {
        AdminBadge(AdminT.inStop(stopped), if (stopped > 0) "danger" else "success")
        AdminBadge(AdminT.totalDishes(allItems.size), "neutral")
    }

    TextInput(value = search, onValueChange = { search = it }, placeholder = AdminT.searchDishPh)

    val visible = allItems
        .filter { search.isBlank() || it.name.localized("uk").contains(search, true) }
        .sortedBy { it.isAvailable }

    if (menu != null && visible.isEmpty()) {
        AdminEmpty(MenuIcons.Search, AdminT.nothingFound, AdminT.tryAnotherQuery)
    }

    visible.forEach { item ->
        Row(
            Modifier
                .fillMaxWidth()
                .backgroundColor(Brand.Surface)
                .borderRadius(14.px)
                .padding(10.px)
                .margin(bottom = 8.px)
                .attrsModifier { classes("qk-adm-card") },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                item.photoUrl?.let { photo ->
                    Image(
                        src = photo,
                        modifier = Modifier.size(42.px).minWidth(42.px).borderRadius(10.px)
                            .objectFit(ObjectFit.Cover).margin(right = 10.px)
                            .styleModifier { if (!item.isAvailable) property("filter", "grayscale(1)") },
                    )
                }
                Column {
                    SpanText(
                        item.name.localized("uk"),
                        Modifier.fontSize(14.px).fontWeight(FontWeight.Medium).color(Brand.Charcoal),
                    )
                    if (!item.isAvailable) {
                        SpanText(AdminT.outOfStock, Modifier.fontSize(11.px).color(Brand.CoralText))
                    }
                }
            }
            AdminToggle(checked = item.isAvailable) { newValue ->
                scope.launch {
                    val ok = runCatching { repository.setAvailability(rid, item.id, newValue) }.isSuccess
                    if (ok) {
                        toast.success(
                            if (newValue) AdminT.backInMenu(item.name.localized("uk")) else AdminT.putToStop,
                        )
                    } else {
                        toast.error(AdminT.updateFailed)
                    }
                    reload()
                }
            }
        }
    }
}

// ---------- Tables & QR ----------

@Composable
fun TablesTab(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    restaurant: com.qrkontaq.data.dto.AdminRestaurantDto,
    role: String,
) {
    val canManage = role == "OWNER" || role == "MANAGER"
    // Cooks and bartenders watch the floor read-only: tapping a table does nothing for them.
    val canOpen = role != "COOK" && role != "BARTENDER"
    // The "add table" popup reuses the shared menu-modal styling.
    InjectMenuStyles()

    var tables by remember(rid) { mutableStateOf<List<TableDto>>(emptyList()) }
    var newLabel by remember { mutableStateOf("") }
    var addingTable by remember(rid) { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val toast = LocalToastState.current

    var calls by remember(rid) { mutableStateOf<List<WaiterCallDto>>(emptyList()) }
    var selectedTable by remember(rid) { mutableStateOf<TableDto?>(null) }

    fun reload() {
        scope.launch { tables = runCatching { repository.tables(rid) }.getOrDefault(emptyList()) }
    }
    LaunchedEffect(rid) {
        reload()
    }

    fun pull() {
        scope.launch {
            calls = runCatching { repository.waiterCalls(rid, status = "OPEN") }.getOrDefault(calls)
            OrdersStore.refresh(repository, rid)
        }
    }

    // Instant updates over WebSocket; polling stays as a slow fallback.
    StaffAlerts.arm(rid)
    var prevCalls by remember(rid) { mutableStateOf(-1) }
    RealtimeUpdates(rid) { pull() }
    LaunchedEffect(rid) {
        while (true) {
            calls = runCatching { repository.waiterCalls(rid, status = "OPEN") }.getOrDefault(calls)
            OrdersStore.refresh(repository, rid)
            kotlinx.coroutines.delay(15_000)
        }
    }
    // Ring + notify only when the number of open calls grows (any transport).
    LaunchedEffect(calls.size) {
        if (prevCalls in 0 until calls.size) {
            StaffAlerts.beep()
            StaffAlerts.notify(AdminT.hallCalls, AdminT.callsWaiter)
        }
        prevCalls = calls.size
    }

    WaiterCallsCard(calls, onResolve = { call ->
        StaffAlerts.suppress()
        scope.launch {
            val response = runCatching { repository.resolveWaiterCall(rid, call.id) }.getOrNull()
            // 404 = someone else already resolved it — the call is gone either way.
            val ok = response != null && (response.code in 200..299 || response.code == 404)
            if (ok) {
                calls = calls.filter { it.id != call.id }
                toast.success(AdminT.markedDone)
            } else {
                toast.error(AdminT.callUpdateFailed)
            }
        }
    })

    if (addingTable && canManage) {
        val close: () -> Unit = {
            addingTable = false
            newLabel = ""
            error = null
        }
        val submit: () -> Unit = {
            if (newLabel.isNotBlank()) {
                scope.launch {
                    val response = runCatching { repository.createTable(rid, newLabel) }.getOrNull()
                    if (response?.data != null) {
                        newLabel = ""
                        error = null
                        addingTable = false
                        reload()
                    } else {
                        error = response?.errors?.firstOrNull() ?: AdminT.addTableFailed
                    }
                }
            }
        }
        Div({
            classes("qk-menu-overlay")
            onClick { close() }
        }) {
            Div({
                classes("qk-menu-modal")
                // Clicks inside the card must not bubble up to the backdrop.
                onClick { it.stopPropagation() }
            }) {
                Div({ classes("qk-menu-modal-head") }) {
                    Div({ classes("t") }) {
                        Div({ classes("i") }) { InlineIcon(AdminIcons.Plus, 18, "#FFFFFF") }
                        Span { Text(AdminT.addTableTitle) }
                    }
                    Button({
                        classes("qk-menu-modal-x")
                        attr("aria-label", AdminT.closeLabel)
                        onClick { close() }
                    }) { InlineIcon(MenuIcons.Close, 16, "#6B7280") }
                }
                Div({ classes("qk-menu-modal-body") }) {
                    TextInput(
                        value = newLabel,
                        onValueChange = { newLabel = it },
                        label = AdminT.nameLabel,
                        placeholder = AdminT.tableLabelPh,
                    )
                    AdminError(error)
                    Row(
                        Modifier.fillMaxWidth().gap(8.px),
                        horizontalArrangement = Arrangement.End,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        AdminButton(
                            text = AdminT.cancel,
                            secondary = true,
                            onClick = close,
                        )
                        AdminButton(
                            text = AdminT.add,
                            iconSvg = AdminIcons.Plus,
                            enabled = newLabel.isNotBlank(),
                            onClick = submit,
                        )
                    }
                }
            }
        }
    }

    if (tables.isNotEmpty()) {
        AdminCard {
            Row(
                Modifier.fillMaxWidth().margin(bottom = 12.px).gap(10.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // "Tables (12)" never wraps — the count stays glued to the word.
                SpanText(
                    AdminT.tablesCount(tables.size),
                    Modifier.fontSize(17.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal)
                        .attrsModifier { classes("qk-nowrap") },
                )
                if (canManage) {
                    // One right-aligned row that never wraps; "add" is a compact green "+".
                    Row(
                        Modifier.gap(8.px).styleModifier { property("flex", "none") },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        if (!addingTable) {
                            Box(
                                Modifier
                                    .size(40.px)
                                    .minWidth(40.px)
                                    .borderRadius(11.px)
                                    .backgroundColor(Brand.Emerald)
                                    .cursor(Cursor.Pointer)
                                    .attrsModifier {
                                        classes("qk-press")
                                        attr("role", "button")
                                        attr("aria-label", AdminT.addTableTitle)
                                    }
                                    .onClick { addingTable = true },
                                contentAlignment = Alignment.Center,
                            ) {
                                InlineIcon(AdminIcons.Plus, 18, "#FFFFFF")
                            }
                        }
                        AdminButton(
                            text = AdminT.qrPdf,
                            iconSvg = AdminIcons.QrCode,
                            secondary = true,
                            onClick = {
                                toast.info(AdminT.preparingPdf)
                                scope.launch { runCatching { repository.downloadQrPdf(rid) } }
                            },
                        )
                    }
                }
            }
            val ordersByTable = OrdersStore.current(rid).groupBy { it.tableId }

            if (canManage) AdminHint(AdminT.planHint)
            TableFloorPlan(
                rid = rid,
                tables = tables,
                calls = calls,
                orders = ordersByTable,
                canDrag = canManage,
                onOpen = if (canOpen) { table -> selectedTable = table } else null,
                onLayoutSave =
                    if (!canManage) {
                        null
                    } else {
                        { fullLayout ->
                            // Only ids that still exist: the local cache may hold deleted tables.
                            val positions =
                                fullLayout
                                    .filterKeys { id -> tables.any { it.id == id } }
                                    .map { (id, pos) -> TablePositionDto(id, pos.first, pos.second) }
                            if (positions.isNotEmpty()) {
                                scope.launch {
                                    val response =
                                        runCatching { repository.saveTableLayout(rid, positions) }.getOrNull()
                                    if (response == null || response.code !in 200..299) {
                                        toast.error(AdminT.layoutSaveFailed)
                                    }
                                }
                            }
                        }
                    },
            )
        }
    } else {
        if (!addingTable && canManage) {
            Box(Modifier.margin(bottom = 14.px)) {
                AdminButton(
                    text = "＋ ${AdminT.addTableTitle}",
                    onClick = { addingTable = true },
                )
            }
        }
        AdminEmpty(
            AdminIcons.QrCode,
            AdminT.noTablesTitle,
            AdminT.noTablesHint,
        )
    }

    selectedTable?.let { table ->
        TableOrdersSheet(
            repository = repository,
            rid = rid,
            role = role,
            restaurant = restaurant,
            table = table,
            onClose = { selectedTable = null },
            onDelete =
                if (!canManage) {
                    null
                } else {
                    {
                        if (window.confirm(AdminT.deleteTableConfirm(table.label))) {
                            scope.launch {
                                runCatching { repository.deleteTable(rid, table.id) }
                                selectedTable = null
                                reload()
                            }
                        }
                    }
                },
        )
    }
}

// ---------- Staff ----------

@Composable
fun StaffTab(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    isPro: Boolean,
    onUpgrade: () -> Unit,
) {
    var staff by remember(rid) { mutableStateOf<StaffDto?>(null) }
    var inviteRole by remember { mutableStateOf("WAITER") }
    var lastInviteUrl by remember { mutableStateOf<String?>(null) }
    var copied by remember { mutableStateOf(false) }
    val toast = LocalToastState.current

    fun reload() {
        scope.launch { staff = runCatching { repository.staff(rid) }.getOrNull() }
    }
    LaunchedEffect(rid) { reload() }

    InjectTeamStyles()

    Div(attrs = { classes("qk-tm") }) {
        val data = staff

        // ---- Hero: member count + overlapping avatar stack over an emerald card ----
        if (data == null) {
            Div(attrs = {
                classes("qk-skeleton")
                attr("style", "height:118px;border-radius:22px;margin-bottom:16px")
            }) {}
        } else {
            Div(attrs = { classes("qk-tm-hero") }) {
                Div(attrs = { classes("qk-tm-top") }) {
                    Span(attrs = { classes("over") }) { Text(AdminT.tabStaff) }
                }
                Div(attrs = { classes("qk-tm-main") }) {
                    Div(attrs = { classes("qk-tm-hl") }) {
                        Div(attrs = { classes("cr") }) {
                            Span(attrs = { classes("num", "qk-tm-disp") }) { Text("${data.members.size}") }
                            Span(attrs = { classes("word") }) { Text(AdminT.teamMembersWord) }
                        }
                        Span(attrs = { classes("sub") }) { Text(AdminT.tabStaffSub) }
                    }
                    if (data.members.isNotEmpty()) {
                        Div(attrs = { classes("qk-tm-stack") }) {
                            val shown = data.members.take(5)
                            shown.forEachIndexed { i, m ->
                                Div(attrs = {
                                    classes("a")
                                    attr("style", avatarStyle(m) + ";z-index:${shown.size - i}")
                                }) { if (m.avatarUrl.isNullOrBlank()) Text(memberInitials(memberDisplay(m))) }
                            }
                            if (data.members.size > 5) {
                                Div(attrs = { classes("a", "more"); attr("style", "z-index:0") }) {
                                    Text("+${data.members.size - 5}")
                                }
                            }
                        }
                    }
                }
            }
        }

        // ---- Invite panel: the roles are always on display; creating invites is Pro-only. ----
        Div(attrs = { classes("qk-tm-card") }) {
            Div(attrs = { classes("cap") }) { Text(AdminT.inviteTitle) }
            Div(attrs = { classes("hint") }) { Text(AdminT.inviteHint) }
            Div(attrs = { classes("qk-tm-roles") }) {
                listOf(
                    Triple("WAITER", AdminT.roleWaiter, AdminT.permWaiter),
                    Triple("COOK", AdminT.roleCook, AdminT.permCook),
                    Triple("BARTENDER", AdminT.roleBartender, AdminT.permBartender),
                    Triple("MANAGER", AdminT.roleManager, AdminT.permManager),
                ).forEach { (value, label, perm) ->
                    val selected = inviteRole == value
                    Div(attrs = {
                        classes("qk-tm-role")
                        if (selected) classes("sel")
                        attr("style", "--rc:${roleColor(value)}")
                        attr("role", "button")
                        onClick { inviteRole = value }
                    }) {
                        Div(attrs = { classes("ic") }) { InlineIcon(roleIcon(value), 17, "currentColor") }
                        Div(attrs = { classes("rt") }) {
                            Span(attrs = { classes("rn") }) { Text(label) }
                            Span(attrs = { classes("rp") }) { Text(perm) }
                        }
                        Div(attrs = { classes("rd") }) {}
                    }
                }
            }
            if (isPro) {
                Button(attrs = {
                    classes("qk-tm-btn")
                    attr("type", "button")
                    onClick {
                        scope.launch {
                            val response = runCatching { repository.createInvite(rid, inviteRole) }.getOrNull()
                            lastInviteUrl = response?.data?.url
                            copied = false
                            if (response?.data != null) {
                                toast.success(AdminT.inviteCreated)
                            } else {
                                toast.error(AdminT.inviteCreateFailed)
                            }
                            reload()
                        }
                    }
                }) {
                    InlineIcon(AdminIcons.Plus, 17, "#FFFFFF")
                    Text(AdminT.createInvite)
                }
            } else {
                // The picker above shows what Pro unlocks; the CTA leads to the plans.
                Button(attrs = {
                    classes("qk-tm-btn", "pro")
                    attr("type", "button")
                    onClick { onUpgrade() }
                }) {
                    InlineIcon(AdminIcons.Lock, 15, "#FFFFFF")
                    Text(AdminT.proUpgradeCta)
                    Span(attrs = { classes("qk-tm-propill") }) { Text("Pro") }
                }
                Div(attrs = {
                    classes("hint")
                    attr("style", "margin-top:10px")
                }) { Text(AdminT.proLockTeamBody) }
            }
            lastInviteUrl?.let { url ->
                Div(attrs = { classes("qk-tm-link") }) {
                    Span(attrs = { classes("u") }) { Text(url) }
                    Div(attrs = {
                        classes("cp")
                        attr("role", "button")
                        onClick {
                            window.navigator.clipboard.writeText(url)
                            copied = true
                        }
                    }) {
                        InlineIcon(if (copied) AdminIcons.Check else AdminIcons.Copy, 14, "currentColor")
                        Text(if (copied) AdminT.copied else AdminT.copy)
                    }
                }
            }
        }

        // ---- Roster + pending invites ----
        data?.let { d ->
            Div(attrs = { classes("qk-tm-card") }) {
                Div(attrs = { classes("qk-tm-sec") }) { Text(AdminT.teamCount(d.members.size)) }
                Div(attrs = { classes("qk-tm-list") }) {
                    d.members.forEach { member ->
                        val display = memberDisplay(member)
                        Div(attrs = {
                            classes("qk-tm-mem")
                            if (member.role == "OWNER") classes("owner")
                            attr("style", "--rc:${roleColor(member.role)}")
                        }) {
                            Div(attrs = { classes("qk-tm-av"); attr("style", avatarStyle(member)) }) {
                                if (member.avatarUrl.isNullOrBlank()) Text(memberInitials(display))
                            }
                            Div(attrs = { classes("qk-tm-mi") }) {
                                Div(attrs = { classes("qk-tm-nm") }) { Text(display) }
                                memberMeta(member)?.let { Div(attrs = { classes("qk-tm-meta") }) { Text(it) } }
                            }
                            Div(attrs = { classes("qk-tm-right") }) {
                                Span(attrs = { classes("qk-tm-chip") }) { Text(roleLabel(member.role)) }
                                if (member.role != "OWNER") {
                                    Button(attrs = {
                                        classes("qk-tm-rm")
                                        attr("type", "button")
                                        attr("aria-label", AdminT.removeFromTeam)
                                        onClick {
                                            if (window.confirm(AdminT.removeFromTeamConfirm)) {
                                                scope.launch {
                                                    runCatching { repository.removeMember(rid, member.userId) }
                                                    reload()
                                                }
                                            }
                                        }
                                    }) { InlineIcon(AdminIcons.Trash, 15, "currentColor") }
                                }
                            }
                        }
                    }
                }
            }

            if (d.activeInvites.isNotEmpty()) {
                Div(attrs = { classes("qk-tm-card") }) {
                    Div(attrs = { classes("qk-tm-sec") }) { Text(AdminT.activeInvites.trimEnd(':', ' ')) }
                    d.activeInvites.forEach { invite ->
                        Div(attrs = {
                            classes("qk-tm-pend")
                            attr("style", "--rc:${roleColor(invite.role)}")
                        }) {
                            Div(attrs = { classes("pic") }) { InlineIcon(roleIcon(invite.role), 16, "currentColor") }
                            Div(attrs = { classes("pt") }) {
                                Div(attrs = { classes("pr") }) { Text(roleLabel(invite.role)) }
                                Div(attrs = { classes("pv") }) { Text(AdminT.validUntil(invite.expiresAt.take(10))) }
                            }
                            Div(attrs = {
                                classes("pc")
                                attr("role", "button")
                                onClick {
                                    scope.launch {
                                        runCatching { repository.deleteInvite(rid, invite.id) }
                                        reload()
                                    }
                                }
                            }) { Text(AdminT.cancel) }
                        }
                    }
                }
            }
        }
    }
}

private fun memberDisplay(m: StaffMemberDto): String =
    m.fullName ?: m.email ?: m.userId.take(8)

private fun memberMeta(m: StaffMemberDto): String? =
    m.email?.takeIf { m.fullName != null }
        ?: m.memberSince.takeIf { it.isNotBlank() }?.let { AdminT.teamMemberSince(it.take(10)) }

/** Two-letter initials from a display name (falls back to the first glyph). */
private fun memberInitials(name: String): String {
    val parts = name.trim().split(Regex("\\s+")).filter { it.isNotEmpty() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].take(2).uppercase()
        else -> (parts[0].take(1) + parts[1].take(1)).uppercase()
    }
}

/** Avatar background: the photo if we have one, otherwise a role-tinted gradient. */
private fun avatarStyle(m: StaffMemberDto): String =
    if (!m.avatarUrl.isNullOrBlank()) {
        "background-image:url('${m.avatarUrl}')"
    } else {
        "background-image:linear-gradient(140deg,${roleColor(m.role)},${roleColorLight(m.role)})"
    }

private fun roleColor(role: String): String =
    when (role) {
        "OWNER" -> "#0F9D6E"
        "MANAGER" -> "#E8A33D"
        "COOK" -> "#FF7A59"
        "BARTENDER" -> "#2FA4C7"
        "WAITER" -> "#7C6BF0"
        else -> "#6B7280"
    }

private fun roleColorLight(role: String): String =
    when (role) {
        "OWNER" -> "#34D399"
        "MANAGER" -> "#F2C078"
        "COOK" -> "#FFB199"
        "BARTENDER" -> "#7ECBE0"
        "WAITER" -> "#A99BF6"
        else -> "#B4BAC2"
    }

private fun roleIcon(role: String): String =
    when (role) {
        "MANAGER" -> AdminIcons.Sliders
        "COOK" -> AdminIcons.ChefHat
        "BARTENDER" -> AdminIcons.Martini
        else -> AdminIcons.Users
    }

// ---------- Subscription ----------

@Composable
fun SubscriptionTab(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
) {
    InjectSubscriptionStyles()
    var info by remember(rid) { mutableStateOf<SubscriptionInfoDto?>(null) }
    var busy by remember { mutableStateOf(false) }

    suspend fun reload() {
        info = runCatching { repository.subscription(rid) }.getOrNull()
    }
    LaunchedEffect(rid) { reload() }

    info?.let { data ->
        val sub = data.subscription
        val isPro = sub.status == "ACTIVE" || sub.status == "PAST_DUE"

        Div({ classes("qk-sub") }) {
            when (sub.status) {
                "ACTIVE" ->
                    MembershipCard(sub) { enable ->
                        scope.launch {
                            runCatching { repository.setAutoRenew(rid, enable) }
                            reload()
                        }
                    }
                "PAST_DUE" ->
                    Div({ classes("qk-sub-warn") }) {
                        Span({ classes("txt") }) { Text(AdminT.pastDueLong) }
                        Span({ classes("badge") }) { Text(AdminT.badgePastDue) }
                    }
            }

            if (!isPro) {
                Div({ classes("qk-sub-head") }) {
                    Div({ classes("qk-sub-eyebrow") }) { Text(AdminT.subEyebrow) }
                    Div({ classes("qk-sub-title", "qk-sub-disp") }) { Text(AdminT.subChooseTitle) }
                    Div({ classes("qk-sub-sub") }) { Text(AdminT.subChooseSub) }
                }

                // Free vs Pro: the venue is already on Free, Pro is the only purchase.
                Div({ classes("qk-sub-plans") }) {
                    Div({ classes("qk-sub-plan") }) {
                        Span({ classes("ribbon") }) { Text(AdminT.planCurrent) }
                        Div({
                            style {
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "9px")
                            }
                        }) {
                            Span({ classes("name") }) { Text(AdminT.planFree) }
                        }
                        Div({ classes("price") }) { B { Text("0 €") } }
                        Div({ classes("per") }) { Text(AdminT.planFreeSub) }
                    }
                    Div({ classes("qk-sub-plan", "sel", "best") }) {
                        Span({ classes("ribbon") }) { Text(AdminT.planRecommended) }
                        Div({
                            style {
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "9px")
                            }
                        }) {
                            Span({ classes("name") }) { Text(AdminT.planPro) }
                        }
                        Div({ classes("price") }) { B { Text("14.99 €") } }
                        Div({ classes("per") }) { Text(AdminT.perMonth) }
                    }
                }

                Button({
                    classes("qk-sub-cta")
                    onClick {
                        if (!busy) {
                            checkout(repository, scope, rid, "MONTH", true) { busy = it != null }
                        }
                    }
                }) {
                    if (busy) {
                        Div({ classes("qk-sub-spin") })
                    } else {
                        Text("${AdminT.subCtaSubscribe} · 14.99 €")
                    }
                }

                Div({ classes("qk-sub-trust") }) {
                    listOf(AdminT.trustCancel, AdminT.trustSecure).forEach { badge ->
                        Div({ classes("t") }) {
                            I { Text("✓") }
                            Span { Text(badge) }
                        }
                    }
                }

                Div({ classes("qk-sub-feat") }) {
                    Div({ classes("cap") }) { Text(AdminT.subFeaturesCap) }
                    AdminT.subFeatures.forEach { f ->
                        Div({ classes("f") }) {
                            I { Text("✓") }
                            Span { Text(f) }
                        }
                    }
                }
            }

            if (data.payments.isNotEmpty()) {
                Div({ classes("qk-sub-hist") }) {
                    Div({ classes("h") }) { Text(AdminT.paymentHistory) }
                    data.payments.forEach { payment ->
                        Div({ classes("r") }) {
                            Span({ classes("d") }) {
                                Text("${payment.createdAt.take(10)} · ${planLabel(payment.plan)}")
                            }
                            Div({ classes("a") }) {
                                Span({ classes("amt") }) { Text(paymentAmount(payment)) }
                                val cls = when (payment.status) {
                                    "SUCCESS", "PAID" -> "ok"
                                    "FAILED", "REVERSED", "EXPIRED" -> "no"
                                    else -> "mid"
                                }
                                Span({ classes("pill", cls) }) { Text(payment.status) }
                            }
                        }
                    }
                }
            }
        }
    } ?: Box(Modifier.fillMaxWidth().height(120.px).borderRadius(16.px).attrsModifier { classes("qk-skeleton") })
}

@Composable
private fun MembershipCard(
    sub: SubscriptionDto,
    onSetAutoRenew: (Boolean) -> Unit,
) {
    Div({ classes("qk-mcard") }) {
        Div({ classes("over") }) { Text(AdminT.subActiveOverline.uppercase()) }
        Div({ classes("row") }) {
            Div({ classes("plan") }) { Text(sub.plan?.let(::planLabel) ?: "—") }
            if (sub.autoRenew && sub.cardMask != null) {
                Div({ classes("chip") }) { Text("•••• ${sub.cardMask}") }
            }
        }
        Div({ classes("next") }) {
            Text(
                if (sub.autoRenew) {
                    AdminT.nextChargeOn(sub.paidUntil?.take(10) ?: "—", "${planPriceEur(sub.plan)} €")
                } else {
                    AdminT.autoRenewOffNote(sub.paidUntil?.take(10) ?: "—")
                },
            )
        }
        Div({ classes("acts") }) {
            when {
                sub.autoRenew ->
                    Span({ classes("link"); onClick { onSetAutoRenew(false) } }) { Text(AdminT.cancelSubscription) }
                sub.cardMask != null ->
                    Span({ classes("link"); onClick { onSetAutoRenew(true) } }) { Text(AdminT.resumeAutoRenew) }
            }
        }
    }
}

private fun planLabel(plan: String): String =
    when (plan) {
        "MONTH" -> AdminT.plan1Month
        "QUARTER" -> AdminT.plan3Months
        "YEAR" -> AdminT.plan12Months
        else -> plan
    }

private fun planPriceEur(plan: String?): String =
    when (plan) {
        "MONTH" -> "25"
        // Legacy periods: no longer purchasable, kept for old auto-renew notes.
        "QUARTER" -> "74.99"
        "YEAR" -> "259.99"
        else -> "0"
    }

/** Minor units + stored currency → "29.99 €" (new payments) or "399 ₴" (legacy UAH rows). */
private fun paymentAmount(payment: PaymentDto): String {
    val whole = payment.amountMinor / 100
    val cents = payment.amountMinor % 100
    val number = if (cents == 0) "$whole" else "$whole.${cents.toString().padStart(2, '0')}"
    return if (payment.currency == "EUR") "$number €" else "$number ₴"
}

private fun checkout(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    plan: String,
    autoRenew: Boolean,
    setBusy: (String?) -> Unit,
) {
    setBusy(plan)
    scope.launch {
        val result =
            runCatching {
                repository.checkout(rid, plan, autoRenew, adminLanguage.code)
            }.getOrNull()
        setBusy(null)
        result?.paymentUrl?.let { window.location.href = it }
    }
}
